import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { format, parseISO } from 'date-fns';
import type { Locale } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Circle, CircleDot, TriangleAlert, UserX, X } from 'lucide-react';

import type { RegistrationCancelPayload, RegistrationNotifyChannel } from '../../../sdk/@types/Simulado';
import { InscritoAvatar } from './InscritoAvatar';
import { InscritoStatusBadge } from './InscritoStatusBadge';
import { CANCEL_REASONS } from './inscritosVocabulary';
import type { DerivedRegistrationStatus, Fase, RegistrationCancelReason } from './inscritosVocabulary';

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60 ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-surface';

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

const BTN =
  'inline-flex h-10 items-center justify-center gap-2 rounded-input px-4 text-sm ' +
  'transition-colors disabled:cursor-not-allowed disabled:opacity-60 ' + FOCUS_RING;

const BTN_NEUTRAL =
  'border border-edge bg-surface font-medium text-ink hover:bg-surface-hover';

/** A única ação destrutiva deste diálogo (design.md Decisão 8) — preenchido, não em contorno. */
const BTN_DANGER_FILLED =
  'border-transparent bg-danger font-semibold text-white hover:brightness-110 dark:text-canvas';

const FIELD_BASE =
  'mt-1.5 w-full rounded-input border border-edge bg-surface px-3 py-2 text-sm text-ink ' +
  'placeholder:text-ink-muted transition-colors ' + FOCUS_RING;

/**
 * As três opções do aviso ao aluno, nesta ordem (spec "Avisar o aluno é
 * opcional e explícito"). Mutuamente exclusivas — por isso um radiogroup, não
 * três caixas independentes.
 */
const NOTIFY_CHANNEL_OPTIONS: { value: RegistrationNotifyChannel; label: string }[] = [
  { value: 'in_app', label: 'Notificação no sistema' },
  { value: 'email', label: 'E-mail' },
  { value: 'none', label: 'Não avisar' },
];

function formatOrRaw(iso: string, pattern: string, locale?: Locale): string {
  try {
    return format(parseISO(iso), pattern, locale ? { locale } : undefined);
  } catch {
    return iso;
  }
}

/**
 * Ficha do aluno mostrada no cartão do diálogo — não o `RegistrationEntry`
 * inteiro, só o que o cartão precisa. `derivedStatus` + `fase` (não um
 * `statusLabel` já pronto) porque o selo é renderizado com `InscritoStatusBadge`,
 * o mesmo componente que Resumo/Tabela/Toolbar usam — quem decide o rótulo por
 * fase (`nao_iniciou` lê "Inscrito" antes do início, "Não iniciou" depois) é
 * `inscritosVocabulary.ts`, num só lugar, nunca este componente.
 */
export interface CancelarInscricaoModalStudent {
  name: string;
  email: string;
  registryDate: string;
  derivedStatus: DerivedRegistrationStatus;
}

/**
 * Uma explicação só, trocada pela seleção atual — não uma legenda por opção
 * sempre visível (spec "Avisar o aluno é opcional e explícito").
 */
function channelExplanation(channel: RegistrationNotifyChannel, student: CancelarInscricaoModalStudent): string {
  switch (channel) {
    case 'in_app':
      return `${student.name} receberá uma notificação dentro do sistema sobre o cancelamento.`;
    case 'email':
      return `Um e-mail avisando sobre o cancelamento será enviado para ${student.email}.`;
    case 'none':
      return 'O aluno não será avisado deste cancelamento.';
  }
}

export interface CancelarInscricaoModalProps {
  open: boolean;
  mocktestName: string;
  /** ISO — início do simulado; é também o limite para o cancelamento. */
  mocktestStart: string;
  /** A mesma fase única que decide Resumo/colunas/ação primária (design.md Decisão 6). */
  fase: Fase;
  student: CancelarInscricaoModalStudent;
  onConfirm: (payload: RegistrationCancelPayload) => void;
  onClose: () => void;
}

/**
 * Diálogo de cancelamento de inscrição — cartão do aluno, janela temporal,
 * motivo obrigatório, observação condicional e a escolha explícita do canal
 * de aviso ao aluno (notificação no sistema, e-mail ou não avisar).
 *
 * Autocontido e sem I/O de propósito: quem chama decide o resultado do
 * cancelamento (409 de janela expirada, falha inesperada, sucesso) porque são
 * efeitos que sobrevivem ao diálogo (atualizar a linha, o `summary`, recarregar
 * a lista) — este componente só recolhe a escolha e a entrega em `onConfirm`.
 *
 * Validação é derivada do estado, não de uma tentativa de confirmar: ao
 * escolher "Outro" a observação já aparece como obrigatória antes de qualquer
 * clique (spec "Motivo 'Outro' sem observação"), e escolher um motivo válido
 * libera a confirmação imediatamente (spec "Motivo escolhido") — então o botão
 * de confirmar fica genuinamente `disabled`, nunca clicável em estado inválido.
 *
 * Sem `Modal`/`Form` do antd, mesmo raciocínio de `ReportQuestionModal`: o
 * `role="dialog"` fica com um elemento só e o componente não depende do
 * `ConfigProvider` para ficar certo no tema escuro em Storybook/testes. Sem
 * variante de bottom sheet — nenhum frame deste change define recorte
 * estreito (proposal.md Non-Goals); se um dia precisar, `ReportQuestionModal`
 * é o precedente.
 */
export default function CancelarInscricaoModal({
  open,
  mocktestName,
  mocktestStart,
  fase,
  student,
  onConfirm,
  onClose,
}: CancelarInscricaoModalProps) {
  const [reason, setReason] = useState<RegistrationCancelReason | ''>('');
  const [note, setNote] = useState('');
  const [notifyChannel, setNotifyChannel] = useState<RegistrationNotifyChannel>('in_app');

  const ids = useId();
  const titleId = `${ids}-title`;
  const introId = `${ids}-intro`;
  const reasonId = `${ids}-reason`;
  const reasonErrorId = `${ids}-reason-error`;
  const noteId = `${ids}-note`;
  const noteErrorId = `${ids}-note-error`;
  const channelId = `${ids}-channel`;
  const channelExplanationId = `${ids}-channel-explanation`;

  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusTo = useRef<Element | null>(null);
  const channelButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const reasonMissing = reason === '';
  const noteRequired = reason === 'outro';
  const noteMissing = noteRequired && note.trim().length === 0;
  const canConfirm = !reasonMissing && !noteMissing;

  // Cada abertura começa limpa — reabrir para outro aluno (ou o mesmo, depois
  // de fechar) não herda o motivo/observação de uma tentativa anterior.
  useEffect(() => {
    if (!open) return;
    setReason('');
    setNote('');
    setNotifyChannel('in_app');
  }, [open, student.email]);

  useEffect(() => {
    if (!open) return;
    restoreFocusTo.current = document.activeElement;
    panelRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      (restoreFocusTo.current as HTMLElement | null)?.focus?.();
    };
  }, [open]);

  const requestClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        requestClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, requestClose]);

  if (!open) return null;

  // Prende o Tab dentro do painel — o mesmo raciocínio de ReportQuestionModal:
  // sem isto o teclado escaparia para o que está montado por baixo.
  const handlePanelKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;

    const focusables = Array.from(
      panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled])'
      )
    );
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey ? active === first || active === panel : active === last) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  };

  // Roving tabindex do radiogroup "Aviso ao aluno" — mesma mecânica de
  // FilterChipGroup/FilterStatusGroup (seleção segue o foco): só o botão
  // selecionado é tabbable, e a seta move e seleciona no mesmo passo.
  const handleChannelKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const currentIndex = NOTIFY_CHANNEL_OPTIONS.findIndex((option) => option.value === notifyChannel);
    if (currentIndex === -1) return;

    let nextIndex: number;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % NOTIFY_CHANNEL_OPTIONS.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + NOTIFY_CHANNEL_OPTIONS.length) % NOTIFY_CHANNEL_OPTIONS.length;
        break;
      default:
        return;
    }

    event.preventDefault();
    setNotifyChannel(NOTIFY_CHANNEL_OPTIONS[nextIndex].value);
    channelButtonRefs.current[nextIndex]?.focus();
  };

  const handleConfirm = () => {
    onConfirm({ reason: reason as RegistrationCancelReason, note: note.trim(), notifyChannel });
  };

  const registryDateLabel = formatOrRaw(student.registryDate, 'dd/MM/yyyy');
  const startLabel = formatOrRaw(mocktestStart, "dd 'de' MMMM 'às' HH:mm", ptBR);

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={introId}
        tabIndex={-1}
        onKeyDown={handlePanelKeyDown}
        className={cx(
          'flex w-full max-w-[560px] flex-col overflow-hidden rounded-modal border border-edge',
          'max-h-[88vh] bg-surface text-ink shadow-panel outline-none'
        )}
      >
        <div className="flex items-start gap-3 border-b border-edge px-5 py-4">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-chip border border-danger/40 bg-danger/[0.12] text-danger-ink">
            <UserX size={18} aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="m-0 text-base font-semibold text-ink">
              Cancelar inscrição
            </h2>
            <p id={introId} className="m-0 mt-0.5 text-[13px] text-ink-secondary">
              O aluno será removido do simulado &ldquo;{mocktestName}&rdquo; e não poderá mais
              acessá-lo.
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            aria-label="Fechar"
            className={cx(
              'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-chip',
              'text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink',
              FOCUS_RING
            )}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-4">
          <div className="flex items-center gap-3 rounded-card border border-edge bg-surface-muted p-3">
            <InscritoAvatar name={student.name} />
            <div className="min-w-0 flex-1">
              <p className="m-0 truncate text-sm font-semibold text-ink">{student.name}</p>
              <p className="m-0 truncate text-[13px] text-ink-secondary">
                {student.email} · Inscrito em {registryDateLabel}
              </p>
            </div>
            <InscritoStatusBadge derivedStatus={student.derivedStatus} fase={fase} />
          </div>

          <div className="flex items-start gap-2 rounded-input border border-warn/40 bg-warn/[0.12] p-3">
            <TriangleAlert size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-warn-ink" />
            <p className="m-0 text-[13px] leading-snug text-ink-secondary">
              O cancelamento é permitido até{' '}
              <strong className="font-semibold text-warn-ink">{startLabel}</strong>, quando o
              simulado começa. Depois disso, esta ação fica indisponível. A vaga será liberada
              para novas inscrições.
            </p>
          </div>

          <div>
            <label htmlFor={reasonId} className="text-sm font-medium text-ink">
              Motivo do cancelamento
            </label>
            <select
              id={reasonId}
              value={reason}
              onChange={(event) => setReason(event.target.value as RegistrationCancelReason | '')}
              aria-invalid={reasonMissing || undefined}
              aria-describedby={reasonMissing ? reasonErrorId : undefined}
              className={cx(FIELD_BASE, reason === '' && 'text-ink-muted')}
            >
              <option value="" disabled>
                Selecione um motivo
              </option>
              {CANCEL_REASONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {reasonMissing ? (
              <p id={reasonErrorId} role="alert" className="m-0 mt-1 text-[13px] text-danger-ink">
                Escolha um motivo para continuar.
              </p>
            ) : null}
          </div>

          <div>
            <label htmlFor={noteId} className="text-sm font-medium text-ink">
              Observação{' '}
              {noteRequired ? null : <span className="font-normal text-ink-muted">(opcional)</span>}
            </label>
            <textarea
              id={noteId}
              rows={3}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={
                noteRequired
                  ? 'Descreva o motivo — obrigatória para "Outro".'
                  : 'Detalhes adicionais, se quiser.'
              }
              aria-required={noteRequired || undefined}
              aria-invalid={noteMissing || undefined}
              aria-describedby={noteMissing ? noteErrorId : undefined}
              className={cx(FIELD_BASE, 'resize-y')}
            />
            {noteMissing ? (
              <p id={noteErrorId} role="alert" className="m-0 mt-1 text-[13px] text-danger-ink">
                A observação é obrigatória para o motivo &ldquo;Outro&rdquo;.
              </p>
            ) : null}
          </div>

          <div>
            <span id={channelId} className="text-sm font-medium text-ink">
              Aviso ao aluno
            </span>
            <div
              role="radiogroup"
              aria-labelledby={channelId}
              aria-describedby={channelExplanationId}
              onKeyDown={handleChannelKeyDown}
              className="mt-1.5 flex w-full flex-col gap-2"
            >
              {NOTIFY_CHANNEL_OPTIONS.map((option, index) => {
                const selected = option.value === notifyChannel;
                return (
                  <button
                    key={option.value}
                    ref={(element) => {
                      channelButtonRefs.current[index] = element;
                    }}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={option.label}
                    tabIndex={selected ? 0 : -1}
                    onClick={() => setNotifyChannel(option.value)}
                    className={cx(
                      'flex w-full items-center gap-2 rounded-input border px-3 py-2 text-left text-sm font-medium transition-colors',
                      FOCUS_RING,
                      selected
                        ? 'border-select/[0.42] bg-select/[0.14] text-select-ink'
                        : 'border-edge bg-surface text-ink hover:bg-surface-hover'
                    )}
                  >
                    {selected ? (
                      <CircleDot size={16} aria-hidden="true" className="shrink-0" />
                    ) : (
                      <Circle size={16} aria-hidden="true" className="shrink-0" />
                    )}
                    <span className="min-w-0">{option.label}</span>
                  </button>
                );
              })}
            </div>
            <p
              id={channelExplanationId}
              className="m-0 mt-1.5 break-words text-[12px] leading-snug text-ink-secondary"
            >
              {channelExplanation(notifyChannel, student)}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-edge bg-surface-muted px-5 py-3">
          <button type="button" onClick={requestClose} className={cx(BTN, BTN_NEUTRAL)}>
            Manter inscrição
          </button>
          <button
            type="button"
            disabled={!canConfirm}
            onClick={handleConfirm}
            className={cx(BTN, BTN_DANGER_FILLED)}
          >
            <UserX size={15} aria-hidden="true" />
            Cancelar inscrição
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
